import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AccountSummary, AuthState } from "@tg/shared";
import { ChatAvatar } from "./components/chat-avatar.js";
import { withChunkRefresh } from "./lib/lazy-retry.js";
import { SiteAuthScreen } from "./components/site-auth-screen.js";
import { ChatView } from "./features/chat/chat-view.js";
import { ChatSidePanel } from "./features/chat/chat-side-panel.js";
import { BotCallbackToasts } from "./features/chat/bot-callback-toasts.js";
import { PendingCallModal } from "./features/chat/pending-call-modal.js";
import { SelfProfilePanel } from "./features/profile/self-profile-panel.js";
import { QuickSwitcher } from "./features/quick-switcher/quick-switcher.js";
import { CommandPalette, openCommandPalette } from "./features/command-palette/command-palette.js";
import { TranslationLanguagePicker } from "./features/chat/translation-language-picker.js";
import { Toasts } from "./components/toasts.js";

const AccountRegistryPanel = lazy(
  withChunkRefresh(() =>
    import("./components/account-registry-panel.js").then((module) => ({
      default: module.AccountRegistryPanel
    }))
  )
);
const SiteSettingsPanel = lazy(
  withChunkRefresh(() =>
    import("./components/site-settings-panel.js").then((module) => ({
      default: module.SiteSettingsPanel
    }))
  )
);
const AuthPanel = lazy(
  withChunkRefresh(() =>
    import("./components/auth-panel.js").then((module) => ({
      default: module.AuthPanel
    }))
  )
);
const LoadDashboardPanel = lazy(
  withChunkRefresh(() =>
    import("./components/load/panel.js").then((module) => ({
      default: module.LoadDashboardPanel
    }))
  )
);
const ShiftDashboard = lazy(withChunkRefresh(() => import("./components/shift-dashboard.js")));
import { requestNotificationPermission } from "./lib/browser-notifications.js";
import { isSameAuthState } from "./lib/auth-flow.js";
import { authApi, httpBaseFromWsUrl, type AppAuthSession } from "./lib/auth-api.js";
import { DialogList, avatarGradientClass } from "./features/dialogs/dialog-list.js";
import { useRealtimeStore, selectUntranslatedCount } from "./lib/store.js";
import { updateTabBadge } from "./lib/tab-badge.js";
import { useSessionBoot } from "./lib/use-session-boot.js";
import { initClientTelemetry } from "./lib/client-telemetry.js";
import {
  LAST_ACCOUNT_STORAGE_KEY,
  readBootAccountId,
  writeWindowAccountId
} from "./lib/window-account.js";
import {
  DIALOGS_PANE_WIDTH_MAX,
  DIALOGS_PANE_WIDTH_MIN,
  useUiPrefs
} from "./lib/ui-prefs.js";
import { formatPeerStatus, type TimeZonePref } from "./lib/datetime.js";
import { countryLabel, formatAutoDeleteShort } from "./lib/country.js";

const connectionStatusLabel: Record<string, string> = {
  connected: "В сети",
  connecting: "Подключаемся…",
  disconnected: "Нет сети"
};

const displayAccountLabel = (account: AccountSummary | null): string =>
  account?.label.trim() ? account.label : "Аккаунт";

export const App = () => {
  // Восстанавливаем выбранный аккаунт через localStorage напрямую —
  // Zustand persist hydrate'ится async и в момент useState() init store
  // ещё пуст, поэтому predшествующий фикс через getState() не сработал.
  // Здесь же значение read-sync на первом render.
  // Мультиокно: аккаунт окна — из адреса (?account=), общий localStorage-ключ
  // только как дефолт для окна без параметра. См. lib/window-account.ts.
  const [accountId, setAccountId] = useState<string | null>(() => readBootAccountId());
  useEffect(() => {
    if (typeof window === "undefined") return;
    writeWindowAccountId(accountId);
    try {
      if (accountId) {
        window.localStorage.setItem(LAST_ACCOUNT_STORAGE_KEY, accountId);
      } else {
        window.localStorage.removeItem(LAST_ACCOUNT_STORAGE_KEY);
      }
    } catch {
      // ignore quota / disabled storage
    }
  }, [accountId]);
  const accountIdRef = useRef<string | null>(accountId);
  accountIdRef.current = accountId;

  // «Назад» (боковая кнопка мыши / Alt+←) закрывает открытый чат, как в
  // оригинальном ТГ. Роутера нет, поэтому открытие чата кладёт запись в
  // History API, а popstate её снимает; закрытие из UI (Esc/крестик)
  // откатывает нашу запись через history.back(), чтобы стек не копился.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const NAV_KEY = "tgClientChatNav";
    let suppressBack = false;
    const isOurEntry = (): boolean =>
      Boolean(window.history.state && (window.history.state as Record<string, unknown>)[NAV_KEY]);
    const unsubscribe = useRealtimeStore.subscribe((state, previous) => {
      if (state.selectedChatId === previous.selectedChatId) return;
      const href = window.location.pathname + window.location.search + window.location.hash;
      if (state.selectedChatId && !previous.selectedChatId) {
        if (isOurEntry()) {
          window.history.replaceState({ [NAV_KEY]: state.selectedChatId }, "", href);
        } else {
          window.history.pushState({ [NAV_KEY]: state.selectedChatId }, "", href);
        }
        return;
      }
      if (state.selectedChatId && previous.selectedChatId) {
        window.history.replaceState({ [NAV_KEY]: state.selectedChatId }, "", href);
        return;
      }
      if (!state.selectedChatId && previous.selectedChatId && isOurEntry() && !suppressBack) {
        suppressBack = true;
        window.history.back();
      }
    });
    const onPopState = (event: PopStateEvent): void => {
      const entry = event.state as Record<string, unknown> | null;
      const chatFromEntry = entry && typeof entry[NAV_KEY] === "string" ? (entry[NAV_KEY] as string) : null;
      // Запись истории могла быть создана в другом аккаунте/до смены —
      // адрес окна всегда возвращаем к текущему аккаунту.
      writeWindowAccountId(accountIdRef.current);
      if (suppressBack) {
        suppressBack = false;
        return;
      }
      const store = useRealtimeStore.getState();
      if (!chatFromEntry) {
        if (store.selectedChatId) {
          suppressBack = true;
          store.selectChat(null);
          suppressBack = false;
        }
        return;
      }
      if (store.selectedChatId !== chatFromEntry && store.chats[chatFromEntry]) {
        store.selectChat(chatFromEntry);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      unsubscribe();
      window.removeEventListener("popstate", onPopState);
    };
  }, []);
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  // Флаг что accounts были загружены хотя бы один раз — нужен, чтобы
  // не сбрасывать persisted accountId до того, как мы реально знаем,
  // существует ли он. Без флага на первом render accounts=[] и эффект
  // ниже стирал accountId, считая что юзер удалил единственный аккаунт.
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [accountSheetOpen, setAccountSheetOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mainMenuOpen, setMainMenuOpen] = useState(false);
  const dialogsPaneWidth = useUiPrefs((state) => state.dialogsPaneWidth);
  const uiScale = useUiPrefs((state) => (state.uiScale > 0 ? state.uiScale : 1));
  const setDialogsPaneWidth = useUiPrefs((state) => state.setDialogsPaneWidth);
  const timeZone: TimeZonePref = useUiPrefs((state) => state.timeZone);
  const setChatHeaderCollapsed = useUiPrefs((state) => state.setChatHeaderCollapsed);
  const chatToolsHidden = useUiPrefs((state) => state.chatToolsHidden);
  // В широком режиме панель профиля (оверлей справа) накрывала бы ленту —
  // по классу отодвигаем колонку чата.
  const setChatToolsHidden = useUiPrefs((state) => state.setChatToolsHidden);
  const rightPanelOpen = useUiPrefs((state) => state.rightPanelOpen);
  const toggleRightPanel = useUiPrefs((state) => state.toggleRightPanel);
  const setRightPanelTab = useUiPrefs((state) => state.setRightPanelTab);
  const openChatSearch = useUiPrefs((state) => state.openChatSearch);
  const dialogsResizeStartRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const isDialogsResizing = useRef(false);
  const [dialogsResizing, setDialogsResizing] = useState(false);
  const handleDialogsResizeStart = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      dialogsResizeStartRef.current = {
        startX: event.clientX,
        startWidth: dialogsPaneWidth
      };
      isDialogsResizing.current = true;
      setDialogsResizing(true);
      (event.currentTarget as HTMLDivElement).setPointerCapture(event.pointerId);
    },
    [dialogsPaneWidth]
  );
  // Во время драга меняем ТОЛЬКО CSS-переменную (ширина панели рисуется через
  // var(--dialogs-pane-width)): коммит в стор + localStorage на каждый
  // pointermove ре-рендерил всё приложение и синхронно писал на диск — на
  // 10k-чатах драг ресайзера сам по себе фризил интерфейс. Стор и localStorage
  // обновляются один раз, на pointerup.
  const dialogsResizeLastWidthRef = useRef<number | null>(null);
  const handleDialogsResizeMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isDialogsResizing.current || !dialogsResizeStartRef.current) return;
      const { startX, startWidth } = dialogsResizeStartRef.current;
      // clientX — визуальные px (с учётом zoom), ширина панели — логические.
      const nextWidth = Math.min(
        DIALOGS_PANE_WIDTH_MAX,
        Math.max(DIALOGS_PANE_WIDTH_MIN, startWidth + (event.clientX - startX) / uiScale)
      );
      dialogsResizeLastWidthRef.current = nextWidth;
      document.documentElement.style.setProperty("--dialogs-pane-width", `${nextWidth}px`);
    },
    [uiScale]
  );
  const handleDialogsResizeEnd = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isDialogsResizing.current) return;
      isDialogsResizing.current = false;
      setDialogsResizing(false);
      dialogsResizeStartRef.current = null;
      if (dialogsResizeLastWidthRef.current !== null) {
        setDialogsPaneWidth(dialogsResizeLastWidthRef.current);
        dialogsResizeLastWidthRef.current = null;
      }
      try {
        (event.currentTarget as HTMLDivElement).releasePointerCapture(event.pointerId);
      } catch {
        // pointer capture может быть уже снят — ок
      }
    },
    [setDialogsPaneWidth]
  );
  const [accountSheetEverOpened, setAccountSheetEverOpened] = useState(false);
  const [settingsEverOpened, setSettingsEverOpened] = useState(false);
  const [loadDashOpen, setLoadDashOpen] = useState(false);
  const [loadDashEverOpened, setLoadDashEverOpened] = useState(false);
  useEffect(() => {
    if (accountSheetOpen) {
      setAccountSheetEverOpened(true);
    }
  }, [accountSheetOpen]);
  useEffect(() => {
    if (settingsOpen) {
      setSettingsEverOpened(true);
    }
  }, [settingsOpen]);
  useEffect(() => {
    if (loadDashOpen) {
      setLoadDashEverOpened(true);
    }
  }, [loadDashOpen]);
  const [selectedAuthState, setSelectedAuthState] = useState<AuthState | null>(null);
  const [appSession, setAppSession] = useState<AppAuthSession | null>(null);
  const [wsUrl] = useState(() =>
    typeof window === "undefined"
      ? "ws://127.0.0.1:8081/ws"
      : `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host || "127.0.0.1:8081"}/ws`
  );
  const connectionStatus = useRealtimeStore((state) => state.connectionStatus);
  const shouldReconnect = useRealtimeStore((state) => state.shouldReconnect);
  const disconnect = useRealtimeStore((state) => state.disconnect);
  const setAppRole = useRealtimeStore((state) => state.setAppRole);
  const setAppUserId = useRealtimeStore((state) => state.setAppUserId);
  useEffect(() => {
    setAppRole(appSession?.user?.role ?? null);
    setAppUserId(appSession?.user?.userId ?? null);
  }, [appSession?.user?.role, appSession?.user?.userId, setAppRole, setAppUserId]);
  // Бейдж «медиа не из шаблонов»: админу показываем счётчик непросмотренных
  // отправок саппортов. Поллинг каждые 20с + при возврате фокуса в окно +
  // мгновенно при открытии главного меню (чтобы точка не отставала от
  // реальности, когда админ прямо сейчас тянется в настройки). Гаснет при
  // открытии журнала в настройках (панель шлёт ack и дёргает onMediaLogSeen).
  const [mediaLogUnseen, setMediaLogUnseen] = useState(0);
  // Бейдж вкладки «Нагрузка»: непросмотренные события воркеров (рестарты,
  // критическая память). Тот же поллинг, что и у медиа-журнала.
  const [workerAlertsUnseen, setWorkerAlertsUnseen] = useState(0);
  // isAdminSession = овнер (Нагрузка, медиа-журнал); менеджерские фичи
  // (просмотр смен команды и т.п.) доступны и гео-админу.
  const isAdminSession = appSession?.user?.role === "admin";
  const isManagerSession =
    appSession?.user?.role === "admin" || appSession?.user?.role === "geo_admin";
  // Технические ошибки realtime-слоя показываем только управляющим: саппорта
  // они пугают, а сделать с ними он всё равно ничего не может.
  const showErrorStack = isManagerSession;
  useEffect(() => {
    // Медиа-журнал открыт и гео-админам (сервер скоупит по их гео);
    // «Нагрузка» (worker-alerts) — только овнеру.
    if (!isManagerSession) {
      setMediaLogUnseen(0);
      setWorkerAlertsUnseen(0);
      return;
    }
    const base = httpBaseFromWsUrl(wsUrl);
    let cancelled = false;
    const poll = (): void => {
      fetch(`${base}/api/admin/media-log/unseen`, { credentials: "same-origin" })
        .then(async (response) =>
          response.ok ? ((await response.json()) as { unseen: number }) : null
        )
        .then((payload) => {
          if (!cancelled && payload && typeof payload.unseen === "number") {
            setMediaLogUnseen(payload.unseen);
          }
        })
        .catch(() => {
          /* сеть моргнула — попробуем в следующем тике */
        });
      if (isAdminSession) {
        fetch(`${base}/api/admin/worker-alerts/unseen`, { credentials: "same-origin" })
          .then(async (response) =>
            response.ok ? ((await response.json()) as { unseen: number }) : null
          )
          .then((payload) => {
            if (!cancelled && payload && typeof payload.unseen === "number") {
              setWorkerAlertsUnseen(payload.unseen);
            }
          })
          .catch(() => {
            /* сеть моргнула — попробуем в следующем тике */
          });
      }
    };
    poll();
    const intervalId = window.setInterval(poll, 20_000);
    window.addEventListener("focus", poll);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", poll);
    };
  }, [isAdminSession, isManagerSession, wsUrl, mainMenuOpen]);
  const errors = useRealtimeStore((state) => state.errors);
  const selectChat = useRealtimeStore((state) => state.selectChat);
  const togglePinned = useRealtimeStore((state) => state.togglePinned);
  const toggleArchived = useRealtimeStore((state) => state.toggleArchived);
  const notificationPermission = useRealtimeStore((state) => state.notificationPermission);
  const notificationsEnabled = useRealtimeStore((state) => state.notificationsEnabled);
  const setNotificationsEnabled = useRealtimeStore((state) => state.setNotificationsEnabled);
  const setChatMuted = useRealtimeStore((state) => state.setChatMuted);
  const refreshNotificationPermission = useRealtimeStore(
    (state) => state.refreshNotificationPermission
  );
  const selectedChatId = useRealtimeStore((state) => state.selectedChatId);
  const placeCall = useRealtimeStore((state) => state.placeCall);
  const setChatTranslation = useRealtimeStore((state) => state.setChatTranslation);
  const chatTranslated = useRealtimeStore((state) =>
    state.selectedChatId ? Boolean(state.translatedChats[state.selectedChatId]) : false
  );
  // «Добрать · N»: сколько сообщений открытого чата ещё не переведено, когда
  // перевод всего чата включён. При 0 чип скрыт.
  const untranslatedRemaining = useRealtimeStore((state) =>
    state.selectedChatId && state.translatedChats[state.selectedChatId]
      ? selectUntranslatedCount(state, state.selectedChatId)
      : 0
  );
  // «Страна телефона» юзера в подзаголовке шапки чата (профиль подгружается
  // лениво при выборе личного чата — см. selectChat в store).
  const selectedChatCountry = useRealtimeStore((state) =>
    state.selectedChatId
      ? state.profilesByChat[state.selectedChatId]?.phoneNumberCountry ?? null
      : null
  );
  const selectedChat = useRealtimeStore((state) =>
    state.selectedChatId ? state.chats[state.selectedChatId] ?? null : null
  );
  const dismissError = useRealtimeStore((state) => state.dismissError);
  const dismissAllErrors = useRealtimeStore((state) => state.dismissAllErrors);
  // Показываем последние три плашки; у каждой стабильный id, поэтому крестик
  // закрывает именно ту ошибку, на которую нажали (а не «сдвигает» стек).
  // Скрытые ошибки не подставляются молча — их количество видно в счётчике.
  const errorPreview = useMemo(() => errors.slice(-3), [errors]);
  const hiddenErrorCount = Math.max(0, errors.length - errorPreview.length);
  const selectedAccount = useMemo(
    () => (accountId ? accounts.find((account) => account.accountId === accountId) ?? null : null),
    [accountId, accounts]
  );
  const selectedChatArchived = selectedChat?.listIds.includes("archive") ?? false;
  // Папки открытого чата — в подпись шапки. Раньше принадлежность к папке
  // была видна только шильдиками в списке, и то лишь для папок Telegram:
  // папки, созданные в самом клиенте, не показывались нигде (ТЗ 2026-09-09).
  const dialogListsForHeader = useRealtimeStore((state) => state.dialogLists);
  const localFoldersForHeader = useRealtimeStore((state) => state.localFolders);
  const selectedChatFolders = useMemo(() => {
    if (!selectedChat || !selectedChatId) {
      return [] as string[];
    }
    const names: string[] = [];
    for (const listId of selectedChat.listIds) {
      const list = dialogListsForHeader[listId];
      if (list?.kind === "folder") {
        names.push(list.title);
      }
    }
    for (const folder of localFoldersForHeader) {
      if (folder.includedChatIds.includes(selectedChatId)) {
        names.push(folder.title);
      }
    }
    return names.filter((name, index) => names.indexOf(name) === index);
  }, [selectedChat, selectedChatId, dialogListsForHeader, localFoldersForHeader]);
  // Бейдж вкладки: суммарные непрочитанные без архива. Считается в сторе
  // троттл-подпиской (unreadTotals) — селектор с reduce по 10k чатов
  // выполнялся здесь на каждый set() стора.
  const totalUnread = useRealtimeStore((state) => state.unreadTotals.total);
  useEffect(() => {
    updateTabBadge(totalUnread);
  }, [totalUnread]);
  const selectedChatTitle = selectedChat
    ? selectedChat.isSavedMessages
      ? "Избранное"
      : selectedChat.title
    : "Выберите чат";
  const mobileChatActive = selectedChatId !== null;
  const currentAuthState =
    selectedAuthState && selectedAuthState.accountId === accountId
      ? selectedAuthState
      : selectedAccount
        ? {
            accountId: selectedAccount.accountId,
            step: selectedAccount.authStep,
            updatedAt: selectedAccount.updatedAt,
            isAuthorized: selectedAccount.isAuthorized,
            ...(selectedAccount.lastAuthError ? { error: selectedAccount.lastAuthError } : {})
          }
        : null;
  const accountReady = currentAuthState?.isAuthorized ?? false;
  const activeSessionAccountId = accountId && accountReady ? accountId : null;
  useSessionBoot(activeSessionAccountId, wsUrl);

  // Снимки памяти и падения вкладки уходят в журнал дашборда «Нагрузка»:
  // жалобы вида «клиент вылетает при массовой подготовке пушей» иначе нечем
  // подтвердить — на пустом аккаунте они не воспроизводятся.
  useEffect(
    () =>
      initClientTelemetry(() => {
        const state = useRealtimeStore.getState();
        let messages = 0;
        for (const feed of Object.values(state.messagesByChat)) {
          messages += feed.length;
        }
        return {
          chats: Object.keys(state.chats).length,
          feeds: Object.keys(state.messagesByChat).length,
          messages,
          translations: Object.keys(state.translationsByMessage).length,
          drafts: Object.keys(state.draftsByChat).length,
          accountId: state.accountId || undefined
        };
      }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    void authApi
      .getSession(wsUrl)
      .then((session) => {
        if (!cancelled) {
          setAppSession(session);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAppSession({
            authenticated: false,
            bootstrapRequired: false
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [wsUrl]);

  // Heartbeat «саппорт за клиентом»: раз в минуту и только если за эту минуту
  // были реальные действия (клавиатура/мышь/скролл) и вкладка видима. По нему
  // дашборд смены считает «последнюю активность», настоящее начало смены и
  // время бездействия — раньше это выводилось из команд в чатах, и открытая
  // на ночь вкладка давала смену «с 00:00» на 20 часов.
  const isSupportSession = appSession?.user?.role === "support";
  useEffect(() => {
    if (!appSession?.authenticated || !isSupportSession) {
      return;
    }
    const base = httpBaseFromWsUrl(wsUrl);
    const ping = (): void => {
      void fetch(`${base}/api/support/activity/ping`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({})
      }).catch(() => {
        // Пинг не критичен: молча пропускаем — ошибка сети и так видна.
      });
    };
    let interacted = true;
    const mark = (): void => {
      interacted = true;
    };
    const interactionEvents = ["keydown", "pointerdown", "wheel", "touchstart", "mousemove"];
    for (const name of interactionEvents) {
      window.addEventListener(name, mark, { passive: true });
    }
    ping();
    const timer = window.setInterval(() => {
      if (!interacted || document.visibilityState !== "visible") {
        return;
      }
      interacted = false;
      ping();
    }, 60_000);
    return () => {
      for (const name of interactionEvents) {
        window.removeEventListener(name, mark);
      }
      window.clearInterval(timer);
    };
  }, [appSession?.authenticated, isSupportSession, wsUrl]);

  // После reload AccountRegistryPanel ленив (рендерится только при первом
  // открытии), и до клика по «Аккаунты» список accounts оставался пустым —
  // accountReady=false → WS не подключался → пустой экран. Здесь грузим
  // accounts сразу как сайт-сессия залогинена, чтобы persisted accountId
  // мог сразу резолвиться в активную сессию без явного клика.
  const accountsBootstrappedRef = useRef(false);
  useEffect(() => {
    if (!appSession?.authenticated || accountsBootstrappedRef.current) {
      return;
    }
    accountsBootstrappedRef.current = true;

    let cancelled = false;
    void authApi
      .listAccounts(wsUrl)
      .then((response) => {
        if (cancelled) return;
        setAccounts(response.accounts);
        setAccountsLoaded(true);
      })
      .catch(() => {
        // Ошибка не фатальна: открытие AccountRegistryPanel дотянет список.
        // Но накачка через явный клик уже не нужна для нормального boot —
        // если сеть упала, у юзера и так будут видны проблемы с подключением.
        if (!cancelled) {
          accountsBootstrappedRef.current = false;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [appSession?.authenticated, wsUrl]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const prefetch = (): void => {
      void import("./components/account-registry-panel.js");
      void import("./components/site-settings-panel.js");
      void import("./components/auth-panel.js");
    };
    const idle = (
      window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      }
    ).requestIdleCallback;
    if (idle) {
      const id = idle(prefetch, { timeout: 4000 });
      return () => {
        const cancel = (
          window as Window & { cancelIdleCallback?: (handle: number) => void }
        ).cancelIdleCallback;
        cancel?.(id);
      };
    }
    const timeoutId = window.setTimeout(prefetch, 2000);
    return () => window.clearTimeout(timeoutId);
  }, []);

  const handleAuthStateChange = useCallback((state: AuthState) => {
    setSelectedAuthState((current) => (isSameAuthState(current, state) ? current : state));
    setAccounts((current) => {
      let changed = false;
      const nextAccounts = current.map((account) => {
        if (account.accountId !== state.accountId) {
          return account;
        }

        const { lastAuthError: _previousAuthError, ...accountWithoutAuthError } = account;
        const nextAccount = {
          ...accountWithoutAuthError,
          authStep: state.step,
          isAuthorized: state.isAuthorized,
          updatedAt: state.updatedAt,
          ...(state.error ? { lastAuthError: state.error } : {})
        };

        if (
          nextAccount.authStep !== account.authStep ||
          nextAccount.isAuthorized !== account.isAuthorized ||
          nextAccount.updatedAt !== account.updatedAt ||
          nextAccount.lastAuthError !== account.lastAuthError
        ) {
          changed = true;
          return nextAccount;
        }

        return account;
      });

      return changed ? nextAccounts : current;
    });
  }, []);

  const handleSelectAccount = useCallback(
    (nextAccountId: string | null) => {
      setAccountId(nextAccountId);
      setSelectedAuthState((current) =>
        current && current.accountId === nextAccountId ? current : null
      );
      selectChat(null);
    },
    [selectChat]
  );

  useEffect(() => {
    // Без accountsLoaded мы стирали persisted accountId на первом render
    // (когда AccountRegistryPanel ещё не открывался и accounts=[]) —
    // и юзеру приходилось каждый раз кликать аккаунт после reload.
    if (!accountsLoaded) {
      return;
    }
    if (accountId && accounts.some((account) => account.accountId === accountId)) {
      return;
    }

    if (accounts.length > 0) {
      setAccountId(accounts[0]!.accountId);
      return;
    }

    if (accountId !== null) {
      setAccountId(null);
      setSelectedAuthState(null);
    }
  }, [accountId, accounts, accountsLoaded]);

  // document.title принадлежит updateTabBadge (lib/tab-badge.ts) —
  // здесь только актуализация разрешения на уведомления.
  useEffect(() => {
    refreshNotificationPermission();
    if (typeof document === "undefined") {
      return;
    }

    const sync = (): void => {
      refreshNotificationPermission();
    };

    document.addEventListener("visibilitychange", sync);
    window.addEventListener("focus", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("focus", sync);
    };
  }, [refreshNotificationPermission]);

  // Keep the word «Уведомления» in every state: a bare «Заблокированы»
  // read like a Telegram blocked-users feature, which it is not — this
  // toggles the browser's push notifications for new messages.
  const notificationLabel =
    notificationPermission === "granted"
      ? notificationsEnabled
        ? "Уведомления вкл"
        : "Уведомления выкл"
      : notificationPermission === "denied"
        ? "Уведомления: браузер запретил"
        : notificationPermission === "unsupported"
          ? "Уведомления недоступны"
          : "Включить уведомления";

  const handleLogout = useCallback(() => {
    void authApi.logout(wsUrl).finally(() => {
      disconnect();
      setAccountSheetOpen(false);
      setSettingsOpen(false);
      setAccounts([]);
      setAccountId(null);
      setSearchQuery("");
      setSelectedAuthState(null);
      selectChat(null);
      setAppSession({
        authenticated: false,
        bootstrapRequired: false
      });
    });
  }, [disconnect, selectChat, wsUrl]);

  if (!appSession) {
    return (
      <main className="app-shell">
        <section className="site-access site-access--loading">
          <div className="site-access__card">
            <div className="site-access__hero">
              <div className="site-access__brand">TG</div>
              <h1 className="site-access__title">Загрузка…</h1>
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (!appSession.authenticated) {
    return (
      <main className="app-shell">
        <SiteAuthScreen
          wsUrl={wsUrl}
          bootstrapRequired={appSession.bootstrapRequired}
          onAuthenticated={setAppSession}
        />
      </main>
    );
  }

  const showReconnectBanner =
    Boolean(activeSessionAccountId) &&
    shouldReconnect &&
    connectionStatus !== "connected";

  return (
    <main className="app-shell">
      {showReconnectBanner ? (
        <div className="reconnect-banner" role="status" aria-live="polite">
          <span className="reconnect-banner__dot" />
          <span>
            {connectionStatus === "connecting"
              ? "Подключаемся…"
              : "Связь прервана — восстанавливаем…"}
          </span>
        </div>
      ) : null}
      <Suspense fallback={null}>
        {accountSheetEverOpened ? (
          <AccountRegistryPanel
            open={accountSheetOpen}
            wsUrl={wsUrl}
            selectedAccountId={accountId}
            onSelectAccount={handleSelectAccount}
            onClose={() => setAccountSheetOpen(false)}
            onAccountsChange={(next) => {
              setAccounts(next);
              setAccountsLoaded(true);
            }}
          />
        ) : null}
        {settingsEverOpened ? (
          <SiteSettingsPanel
            open={settingsOpen}
            wsUrl={wsUrl}
            session={appSession}
            onClose={() => setSettingsOpen(false)}
            onSessionChange={setAppSession}
            onMediaLogSeen={() => setMediaLogUnseen(0)}
          />
        ) : null}
        {loadDashEverOpened ? (
          <LoadDashboardPanel
            open={loadDashOpen}
            wsUrl={wsUrl}
            onClose={() => setLoadDashOpen(false)}
            onAlertsSeen={() => setWorkerAlertsUnseen(0)}
          />
        ) : null}
      </Suspense>

      <section className="workspace-frame">
        <aside
          className={
            mobileChatActive ? "dialogs-pane dialogs-pane--mobile-hidden" : "dialogs-pane"
          }
        >
          <header className="pane-header pane-header--dialogs">
            <div className="sidebar-header">
              <div className="ch-menu">
                <button
                  type="button"
                  className="hamburger"
                  onClick={() => setMainMenuOpen((value) => !value)}
                  aria-label="Меню"
                  aria-expanded={mainMenuOpen}
                >
                  <i className="ic ic-menu" aria-hidden="true" />
                  {mediaLogUnseen + workerAlertsUnseen > 0 ? (
                    <span className="menu-badge menu-badge--dot" aria-hidden="true" />
                  ) : null}
                </button>
                {mainMenuOpen ? (
                  <>
                    <div
                      className="ch-menu__layer"
                      onClick={() => setMainMenuOpen(false)}
                      role="presentation"
                    />
                    <div className="ch-menu__list ch-menu__list--left" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        className="ch-menu__item"
                        onClick={() => {
                          setSettingsOpen(false);
                          setAccountSheetOpen(true);
                          setMainMenuOpen(false);
                        }}
                      >
                        <i className="ic ic-user" aria-hidden="true" />
                        Аккаунты
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="ch-menu__item"
                        onClick={() => {
                          setAccountSheetOpen(false);
                          setSettingsOpen(true);
                          setMainMenuOpen(false);
                        }}
                      >
                        <i className="ic ic-settings" aria-hidden="true" />
                        Настройки
                        {mediaLogUnseen > 0 ? (
                          <span className="menu-badge menu-badge--count">
                            {mediaLogUnseen > 99 ? "99+" : mediaLogUnseen}
                          </span>
                        ) : null}
                      </button>
                      {isAdminSession ? (
                        <button
                          type="button"
                          role="menuitem"
                          className="ch-menu__item"
                          onClick={() => {
                            setAccountSheetOpen(false);
                            setSettingsOpen(false);
                            setLoadDashOpen(true);
                            setMainMenuOpen(false);
                          }}
                        >
                          <i className="ic ic-sessions" aria-hidden="true" />
                          Нагрузка
                          {workerAlertsUnseen > 0 ? (
                            <span className="menu-badge menu-badge--count">
                              {workerAlertsUnseen > 99 ? "99+" : workerAlertsUnseen}
                            </span>
                          ) : null}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        role="menuitem"
                        className="ch-menu__item ch-menu__item--danger"
                        onClick={() => {
                          setMainMenuOpen(false);
                          handleLogout();
                        }}
                      >
                        <i className="ic ic-logout" aria-hidden="true" />
                        Выйти
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
              <div className="search">
                <i className="ic ic-search" aria-hidden="true" />
                <input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Поиск"
                  aria-label="Поиск"
                  disabled={!accountReady}
                />
              </div>
              <button
                type="button"
                className="avatar-story"
                onClick={() => {
                  if (accountReady) {
                    useRealtimeStore.getState().openSelfProfile();
                    return;
                  }
                  setSettingsOpen(false);
                  setAccountSheetOpen(true);
                }}
                aria-label="Мой профиль"
                title={
                  selectedAccount
                    ? `${displayAccountLabel(selectedAccount)} · ${connectionStatusLabel[connectionStatus] ?? connectionStatus}`
                    : "Мой профиль"
                }
              >
                <span className="inner">
                  {selectedAccount
                    ? displayAccountLabel(selectedAccount).slice(0, 2).toUpperCase()
                    : "TG"}
                  {selectedAccount ? (
                    <span
                      className={`status-dot status-dot--${connectionStatus}`}
                      aria-label={connectionStatusLabel[connectionStatus] ?? connectionStatus}
                    />
                  ) : null}
                </span>
              </button>
            </div>

            {showErrorStack && errorPreview.length > 0 ? (
              <div className="status-stack" role="status" aria-live="polite">
                {errorPreview.map((notice) => (
                  <div
                    key={notice.id}
                    className="status-stack__item status-stack__item--error"
                  >
                    <span className="status-stack__item-text">
                      {notice.text}
                      {notice.count > 1 ? (
                        <span className="status-stack__item-count"> ×{notice.count}</span>
                      ) : null}
                      {notice.detail ? (
                        <span className="status-stack__item-detail">{notice.detail}</span>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      className="status-stack__item-close"
                      onClick={() => dismissError(notice.id)}
                      aria-label="Скрыть ошибку"
                      title="Скрыть"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {hiddenErrorCount > 0 ? (
                  <div className="status-stack__hidden">
                    и ещё {hiddenErrorCount} — нажмите «Очистить все»
                  </div>
                ) : null}
                {errors.length > 1 ? (
                  <button
                    type="button"
                    className="status-stack__clear"
                    onClick={dismissAllErrors}
                  >
                    Очистить все
                  </button>
                ) : null}
              </div>
            ) : null}
          </header>

          <DialogList searchQuery={searchQuery} />
          <div
            className={
              dialogsResizing
                ? "dialogs-pane__resizer dialogs-pane__resizer--dragging"
                : "dialogs-pane__resizer"
            }
            role="separator"
            aria-orientation="vertical"
            aria-label="Ширина списка чатов"
            onPointerDown={handleDialogsResizeStart}
            onPointerMove={handleDialogsResizeMove}
            onPointerUp={handleDialogsResizeEnd}
            onPointerCancel={handleDialogsResizeEnd}
          />
        </aside>

        <section
          className={[
            "chat-pane",
            mobileChatActive ? "chat-pane--mobile-active" : ""
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <header className="pane-header pane-header--chat">
            {selectedChatId ? (
              <button
                type="button"
                className="hbtn hbtn--back"
                onClick={() => selectChat(null)}
                aria-label="Назад к списку чатов"
              >
                <i className="ic ic-back" aria-hidden="true" />
              </button>
            ) : null}
            {selectedChatId ? (
              <button
                type="button"
                className="chat-summary__open"
                onClick={() => setRightPanelTab("info")}
                aria-label="Открыть профиль чата"
              >
                <ChatAvatar
                  title={selectedChatTitle}
                  avatarUrl={selectedChat?.avatarUrl}
                  className={`chat-summary__avatar ${avatarGradientClass(selectedChatId)}`}
                  savedMessages={selectedChat?.isSavedMessages ?? false}
                >
                  {selectedChat && !selectedChat.isSavedMessages && !selectedChat.isGroupChat ? (
                    // Как в списке чатов: красная точка — собеседник
                    // заблокировал аккаунт, зелёная — в сети.
                    selectedChat.isBlockedByUser ? (
                      <span
                        className="status-dot status-dot--blocked"
                        aria-label="Заблокировал"
                        title="Собеседник заблокировал этот аккаунт"
                      />
                    ) : selectedChat.isPeerOnline ? (
                      <span className="status-dot status-dot--online" aria-label="В сети" title="В сети" />
                    ) : null
                  ) : null}
                </ChatAvatar>
                <div className="ch-info">
                  <div className="ch-title">{selectedChatTitle}</div>
                  <div className="ch-sub">
                    {selectedChat?.isSavedMessages ? (
                      // У «Избранного» нет собеседника — статусы «в сети /
                      // был(а)» тут только путали.
                      "ваши сохранённые сообщения"
                    ) : selectedChat?.isTyping ? (
                      <span className="typing-indicator" aria-label="печатает">
                        <span className="typing-indicator__dot" />
                        <span className="typing-indicator__dot" />
                        <span className="typing-indicator__dot" />
                      </span>
                    ) : (
                      <>
                        {selectedChat?.isBlockedByUser ? (
                          <span
                            className="ch-sub__blocked"
                            title="Telegram отклонил отправку: собеседник заблокировал этот аккаунт"
                          >
                            Заблокировал
                          </span>
                        ) : null}
                        {[
                        selectedChat?.isBlockedByUser
                          ? null
                          : selectedChat?.isPeerOnline
                            ? selectedChat.peerStatusLabel ?? "в сети"
                            : formatPeerStatus(
                                selectedChat?.peerLastSeenAt,
                                selectedChat?.peerStatusLabel,
                                timeZone
                              ),
                        selectedChatCountry ? countryLabel(selectedChatCountry) : null,
                        selectedChat?.autoDeleteTime
                          ? `⏱ автоудаление ${formatAutoDeleteShort(selectedChat.autoDeleteTime)}`
                          : null,
                        selectedChatFolders.length > 0
                          ? `📁 ${selectedChatFolders.join(", ")}`
                          : null,
                        selectedChat && selectedChat.internalTags.length > 0
                          ? selectedChat.internalTags.join(", ")
                          : null
                        ]
                          .filter((value): value is string => Boolean(value))
                          .map((value, index) =>
                            index === 0 && !selectedChat?.isBlockedByUser ? value : ` · ${value}`
                          )
                          .join("")}
                      </>
                    )}
                  </div>
                </div>
              </button>
            ) : (
              <div className="chat-summary__open chat-summary__open--static">
                <ChatAvatar
                  title={selectedChatTitle}
                  avatarUrl={selectedChat?.avatarUrl}
                  className="chat-summary__avatar"
                />
                <div className="ch-info">
                  <div className="ch-title">{selectedChatTitle}</div>
                  <div className="ch-sub">
                    {accountReady ? "Выберите чат слева" : "Подключите аккаунт"}
                  </div>
                </div>
              </div>
            )}
            <div className="ch-actions">
              {/* Открыть «Быстрое меню действий» — фолбэк для тех, у кого
                  ⌘M/Ctrl+M и Ctrl+; перехватываются браузером или
                  корпоративной policy. Всегда видна, независимо от чата. */}
              <button
                type="button"
                className="hbtn"
                onClick={() => openCommandPalette()}
                title="Быстрое меню действий (⌘M / Ctrl+M, альт. Ctrl+;)"
                aria-label="Быстрое меню действий"
              >
                <i className="ic ic-menu" aria-hidden="true" />
              </button>
              {selectedChatId ? (
                <button
                  type="button"
                  className={chatTranslated ? "hbtn hbtn--active" : "hbtn"}
                  onClick={() => setChatTranslation(selectedChatId, !chatTranslated)}
                  title={chatTranslated ? "Перевод включён — показать оригинал" : "Перевести весь чат"}
                  aria-label="Перевести весь чат"
                  aria-pressed={chatTranslated}
                >
                  <i className="ic ic-translate" aria-hidden="true" />
                </button>
              ) : null}
              <TranslationLanguagePicker />
              {selectedChatId && untranslatedRemaining > 0 ? (
                <button
                  type="button"
                  className="topup-chip"
                  onClick={() => setChatTranslation(selectedChatId, true)}
                  title="Перевести оставшиеся сообщения"
                >
                  Добрать · {untranslatedRemaining}
                </button>
              ) : null}
              {selectedChatId &&
              selectedChat &&
              !selectedChat.isGroupChat &&
              !selectedChat.isSavedMessages &&
              selectedChat.telegramUserId ? (
                // Звонок юзеру — в шапке, справа от имени, как в оригинальном TG.
                // Это пропуш: вызов создаётся и сразу сбрасывается.
                <button
                  type="button"
                  className="hbtn hbtn--call"
                  onClick={() => {
                    if (
                      !window.confirm(
                        "Позвонить пользователю? Это пропуш: звонок сразу сбрасывается, у получателя останется уведомление о пропущенном вызове."
                      )
                    ) {
                      return;
                    }
                    placeCall(selectedChatId);
                  }}
                  title="Позвонить (пропуш-звонок)"
                  aria-label="Позвонить"
                >
                  <i className="ic ic-phone" aria-hidden="true" />
                </button>
              ) : null}
              {selectedChatId ? (
                <button
                  type="button"
                  className="hbtn"
                  onClick={() => openChatSearch()}
                  title="Поиск по чату"
                  aria-label="Поиск по чату"
                >
                  <i className="ic ic-search" aria-hidden="true" />
                </button>
              ) : null}
              <button
                type="button"
                className={chatToolsHidden ? "hbtn hbtn--active" : "hbtn"}
                onClick={() => setChatToolsHidden(!chatToolsHidden)}
                title={
                  chatToolsHidden
                    ? "Показать панель инструментов (Поиск, Теги, Удалённые…)"
                    : "Скрыть панель инструментов (Поиск, Теги, Удалённые…)"
                }
                aria-label={
                  chatToolsHidden ? "Показать панель инструментов" : "Скрыть панель инструментов"
                }
                aria-pressed={chatToolsHidden}
              >
                <i className={chatToolsHidden ? "ic ic-eye2" : "ic ic-eye1"} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="hbtn"
                onClick={async () => {
                  if (notificationPermission === "granted") {
                    setNotificationsEnabled(!notificationsEnabled);
                    return;
                  }
                  if (notificationPermission === "denied") {
                    window.alert(
                      "Уведомления о новых сообщениях заблокированы в браузере для этого сайта. " +
                        "Браузер не позволяет запросить их повторно из приложения. " +
                        "Чтобы включить: нажмите значок настроек сайта слева от адресной строки → " +
                        "«Уведомления» → «Разрешить», затем обновите страницу."
                    );
                    return;
                  }
                  await requestNotificationPermission();
                  refreshNotificationPermission();
                  if (Notification.permission === "granted") {
                    setNotificationsEnabled(true);
                  }
                }}
                disabled={notificationPermission === "unsupported"}
                title={notificationLabel}
                aria-label={notificationLabel}
              >
                <i
                  className={
                    notificationPermission === "granted" && notificationsEnabled
                      ? "ic ic-unmute"
                      : "ic ic-mute"
                  }
                  aria-hidden="true"
                />
              </button>
              {selectedChatId && selectedChat && !selectedChat.isSavedMessages ? (
                <button
                  type="button"
                  className={selectedChat.isPinned ? "hbtn hbtn--active" : "hbtn"}
                  onClick={() => togglePinned(selectedChatId, !selectedChat.isPinned)}
                  title={selectedChat.isPinned ? "Открепить чат" : "Закрепить чат"}
                  aria-label={selectedChat.isPinned ? "Открепить чат" : "Закрепить чат"}
                >
                  <i
                    className={selectedChat.isPinned ? "ic ic-pin2" : "ic ic-pin"}
                    aria-hidden="true"
                  />
                </button>
              ) : null}
              {selectedChatId && selectedChat && !selectedChat.isSavedMessages ? (
                <button
                  type="button"
                  className="hbtn"
                  onClick={() => toggleArchived(selectedChatId, !selectedChatArchived)}
                  title={selectedChatArchived ? "Вернуть из архива" : "В архив"}
                  aria-label={selectedChatArchived ? "Вернуть из архива" : "В архив"}
                >
                  <i className="ic ic-folder" aria-hidden="true" />
                </button>
              ) : null}
              {selectedChatId && selectedChat && !selectedChat.isSavedMessages ? (
                <button
                  type="button"
                  className={selectedChat.isMuted ? "hbtn hbtn--active" : "hbtn"}
                  onClick={() => setChatMuted(selectedChatId, !(selectedChat.isMuted ?? false))}
                  title={
                    selectedChat.isMuted
                      ? "Уведомления чата выключены — включить"
                      : "Заглушить уведомления этого чата"
                  }
                  aria-label={
                    selectedChat.isMuted
                      ? "Включить уведомления чата"
                      : "Заглушить уведомления чата"
                  }
                  aria-pressed={selectedChat.isMuted ?? false}
                >
                  <i className="ic ic-muted" aria-hidden="true" />
                </button>
              ) : null}
              {selectedChatId ? (
                <button
                  type="button"
                  className={rightPanelOpen ? "hbtn hbtn--active" : "hbtn"}
                  onClick={toggleRightPanel}
                  title={rightPanelOpen ? "Скрыть панель" : "Показать панель"}
                  aria-label="Правая панель"
                  aria-pressed={rightPanelOpen}
                >
                  <i className="ic ic-more" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          </header>

          {!selectedAccount ? (
            <section className="auth-screen auth-screen--empty">
              <div className="empty-state">
                <div className="empty-state__icon">
                  <i className="ic ic-adduser" aria-hidden="true" />
                </div>
                <h2 className="empty-state__title">Добавьте аккаунт</h2>
                <p className="empty-state__subtitle">
                  Подключите свой первый Telegram-аккаунт, чтобы начать.
                </p>
                <button
                  type="button"
                  className="button button--primary empty-state__button"
                  onClick={() => setAccountSheetOpen(true)}
                >
                  <i className="ic ic-plus" aria-hidden="true" /> Добавить аккаунт
                </button>
              </div>
            </section>
          ) : !accountReady ? (
            <Suspense fallback={null}>
              <AuthPanel
                accountId={selectedAccount.accountId}
                accountLabel={displayAccountLabel(selectedAccount)}
                wsUrl={wsUrl}
                {...(currentAuthState ? { initialAuthState: currentAuthState } : {})}
                onAuthStateChange={handleAuthStateChange}
              />
            </Suspense>
          ) : selectedChatId ? (
            <ChatView />
          ) : (
            // Чат не выбран — вместо пустого «Выберите чат» дашборд смены
            // саппорта (статистика, перерывы, депозиты, график ЗП).
            <Suspense fallback={null}>
              <ShiftDashboard
                wsUrl={wsUrl}
                isAdmin={isManagerSession}
                accountId={selectedAccount.accountId}
              />
            </Suspense>
          )}
        </section>
        <ChatSidePanel />
      </section>
      <QuickSwitcher />
      <SelfProfilePanel />
      <BotCallbackToasts />
      <PendingCallModal />
      <CommandPalette />
      <Toasts />
    </main>
  );
};
